// The <video> element and its controls. Same control vocabulary as the
// investigation transcript's bar (back 10 s, play, forward 10 s, time,
// seek range, rate) plus previous/next point of interest. The element is
// exposed through the ref so the transcript and the marker bar share one
// clock.
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { BackwardIcon, ForwardIcon, PauseIcon, PlayIcon } from "@heroicons/react/24/solid";
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Dropdown from "../../Settings/Dropdown";
import { formatClock } from "./types";

export type VideoPlayerHandle = {
  media: HTMLVideoElement | null;
  seek: (ms: number, play?: boolean) => void;
};

interface Props {
  src: string | null;
  poster?: string;
  durationMs: number;
  initialSeekMs?: number;
  onTime?: (ms: number) => void;
  onPrevPoint?: () => void;
  onNextPoint?: () => void;
  hasPoints?: boolean;
  // Overlaid on the seek bar (the marker layer), so there is one timeline.
  markers?: React.ReactNode;
  // Rendered below the controls row.
  children?: React.ReactNode;
  className?: string;
}

const VideoPlayer = forwardRef<VideoPlayerHandle, Props>(({ src, poster, durationMs, initialSeekMs, onTime, onPrevPoint, onNextPoint, hasPoints, markers, children, className }, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentMs, setCurrentMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [duration, setDuration] = useState(durationMs);

  const seek = (ms: number, play = true) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, ms) / 1000;
    setCurrentMs(ms);
    onTime?.(ms);
    if (play) el.play().catch(() => {});
  };
  useImperativeHandle(ref, () => ({ media: videoRef.current, seek }), []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || initialSeekMs == null) return;
    const h = () => seek(initialSeekMs, false);
    if (el.readyState >= 1) h();
    else el.addEventListener("loadedmetadata", h, { once: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSeekMs, src]);

  const total = duration || durationMs || 1;
  return (
    <div className={cn("flex flex-col", className)}>
      <div data-tour="footage-video" className="relative bg-black rounded-t-xl overflow-hidden aspect-video">
        {src ? (
          <video
            ref={videoRef}
            src={src}
            poster={poster}
            preload="metadata"
            playsInline
            className="w-full h-full"
            onTimeUpdate={(e) => {
              const ms = Math.round(e.currentTarget.currentTime * 1000);
              setCurrentMs(ms);
              onTime?.(ms);
            }}
            onSeeked={(e) => {
              const ms = Math.round(e.currentTarget.currentTime * 1000);
              setCurrentMs(ms);
              onTime?.(ms);
            }}
            onLoadedMetadata={(e) => {
              if (Number.isFinite(e.currentTarget.duration)) setDuration(Math.round(e.currentTarget.duration * 1000));
            }}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onRateChange={(e) => setRate(e.currentTarget.playbackRate)}
            onClick={() => (playing ? videoRef.current?.pause() : videoRef.current?.play().catch(() => {}))}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/70">
            {poster ? <img src={poster} alt="" className="w-full h-full object-contain opacity-60" /> : null}
            <span className="absolute">Playable video is still being prepared.</span>
          </div>
        )}
      </div>
      <div className="border border-t-0 border-base-300 rounded-b-xl bg-base-100">
        <div className="px-3 py-2 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
            onClick={() => seek(Math.max(0, currentMs - 10000), playing)}
            title="Back 10s"
          >
            <BackwardIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors disabled:opacity-40"
            disabled={!src}
            onClick={() => (playing ? videoRef.current?.pause() : videoRef.current?.play().catch(() => {}))}
            title={playing ? "Pause" : "Play"}
          >
            {playing ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4 ml-0.5" />}
          </button>
          <button
            type="button"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
            onClick={() => seek(currentMs + 10000, playing)}
            title="Forward 10s"
          >
            <ForwardIcon className="h-4 w-4" />
          </button>
          <span className="text-sm tabular-nums text-base-content/70">
            {formatClock(currentMs)} / {formatClock(total)}
          </span>
          <div data-tour="footage-seekbar" className="relative flex-1 min-w-32 flex items-center h-6">
            <input
              type="range"
              min={0}
              max={total}
              value={Math.min(currentMs, total)}
              onChange={(e) => seek(Number(e.target.value), playing)}
              className="inv-seek w-full"
              style={{ "--p": `${(Math.min(currentMs, total) / total) * 100}%` } as React.CSSProperties}
            />
            {markers}
          </div>
          <Dropdown
            size="xs"
            contentClassName="w-20"
            options={[1, 1.25, 1.5, 2].map((r) => ({ value: String(r), label: `${r}×` }))}
            value={String(rate)}
            onChange={(v) => {
              const r = Number(v);
              if (videoRef.current) videoRef.current.playbackRate = r;
              setRate(r);
            }}
          />
          {hasPoints && (
            <span data-tour="footage-point-nav" className="inline-flex rounded-md border border-base-300 overflow-hidden">
              <button type="button" className="px-1.5 py-1 text-base-content/60 hover:bg-base-200 transition-colors" onClick={onPrevPoint} title="Previous point of interest">
                <ChevronDoubleLeftIcon className="size-3.5" />
              </button>
              <button type="button" data-tour="footage-next-point" className="px-1.5 py-1 border-l border-base-300 text-base-content/60 hover:bg-base-200 transition-colors" onClick={onNextPoint} title="Next point of interest">
                <ChevronDoubleRightIcon className="size-3.5" />
              </button>
            </span>
          )}
        </div>
        {children}
      </div>
    </div>
  );
});
VideoPlayer.displayName = "VideoPlayer";

export default VideoPlayer;
